import { Fragment, type ReactNode } from 'react';
import {
  IDEAL_PROFILE_BOXES,
  LEADER_FIXED_ITEMS,
  MUST_HAVE_CHECKS,
  MUST_HAVE_CHECK_LABELS,
  PICKABLE_WORK_HABITS,
  PLAN_30_BOXES,
  PLAN_90_BOXES,
  PLAN_CHECKS,
  PLAN_CHECK_LABELS,
  TOOL_NAME_MAX,
  idealProfileProgress,
  plan30Progress,
  plan90Progress,
  type DeliveredRow,
  type HabitRow,
  type IdealProfile,
  type MeetRow,
  type MustHaveRow,
  type NumberRow,
  type Plan30,
  type Plan90,
  type SkillRow,
  type TeamMeasureRow,
  type TemplateProgress,
  type TextRow,
} from '@nest-recruit/shared';
import { AddOnHeading, TemplateBoxFrame, TemplateFields, TemplateRows, type TemplateColumn } from './TemplateBox';
import { ToolRows } from './ToolRows';
import { SuggestInput } from '../../../components/SuggestInput';
import styles from './TemplateForms.module.css';

/**
 * ADR 0083 (owner 2026-11-01 Q4–Q6): the three templates as forms, each a list of boxes exactly as the templates print them.
 * Controlled (value + onChange), so Request a hire (inside its form) and the hire's plans page (on its own) use the same ones.
 * The Leadership add-on's parts join only while the role manages people; switching that off hides them and keeps what was typed.
 */

interface FormProps<T> {
  readonly value: T;
  readonly onChange: (value: T) => void;
  readonly managesPeople: boolean;
  readonly disabled?: boolean;
}

function problemOf(progress: TemplateProgress, box: string): string | null {
  return progress.problems.find((problem) => problem.box === box)?.message ?? null;
}

const text = <R,>(key: keyof R & string, label: string, track?: string): TemplateColumn<R> => ({ key, label, kind: 'text', ...(track ? { track } : {}) });
const emptyText = (): TextRow => ({ text: '' });

// --- The Ideal candidate profile --------------------------------------------------------------------------------------

const MUST_COLUMNS: readonly TemplateColumn<MustHaveRow>[] = [
  text<MustHaveRow>('skill', 'Must-have skill or ability', 'minmax(0, 3fr)'),
  { key: 'check', label: 'How we check it', kind: 'choice', options: MUST_HAVE_CHECKS.map((value) => ({ value, label: MUST_HAVE_CHECK_LABELS[value] })) },
];

/**
 * Evening review 2026-11-01 item 6: the profile's steps, one box each, so Request a hire can show it as Next → Next instead of
 * one long page. The Leadership add-on's Part 1 is one last step on a role that manages people.
 */
export const IDEAL_PROFILE_STEPS = ['tools', 'mustHaves', 'niceToHaves', 'experience', 'qualifications', 'dealBreakers', 'workHabits', 'redFlags', 'leadership'] as const;
export type IdealProfileStep = (typeof IDEAL_PROFILE_STEPS)[number];

export function idealProfileStepsFor(managesPeople: boolean): readonly IdealProfileStep[] {
  return managesPeople ? IDEAL_PROFILE_STEPS : IDEAL_PROFILE_STEPS.filter((step) => step !== 'leadership');
}

/** The boxes each step holds, for its "done" mark. */
export const IDEAL_PROFILE_STEP_BOXES: Readonly<Record<IdealProfileStep, readonly string[]>> = {
  tools: ['tools'],
  mustHaves: ['mustHaves'],
  niceToHaves: ['niceToHaves'],
  experience: ['experience'],
  qualifications: ['qualifications'],
  dealBreakers: ['dealBreakers'],
  workHabits: ['workHabits'],
  redFlags: ['redFlags'],
  leadership: ['leadExperience', 'leadSituation', 'leadHabits'],
};

const NO_NAMES: readonly string[] = [];

/** All the boxes, or only one step's. */
export function IdealProfileForm({
  value,
  onChange,
  managesPeople,
  disabled = false,
  step,
  toolNames = NO_NAMES,
}: FormProps<IdealProfile> & { readonly step?: IdealProfileStep; /** Item 7: the tool names offered in box 1 (ADR 0087). */ readonly toolNames?: readonly string[] }): ReactNode {
  const boxes = IDEAL_PROFILE_BOXES;
  const progress = idealProfileProgress(value, managesPeople);
  const set = <K extends keyof IdealProfile>(key: K) => (next: IdealProfile[K]): void => onChange({ ...value, [key]: next });
  const picked = value.workHabits.map((row) => row.habit);
  const habitColumns: readonly TemplateColumn<HabitRow>[] = [
    {
      key: 'habit',
      label: 'Work habit',
      kind: 'choice',
      track: 'minmax(0, 1.4fr)',
      placeholder: 'Pick a habit',
      options: PICKABLE_WORK_HABITS.map((habit) => ({ value: habit.key, label: habit.label, description: habit.question, disabled: picked.includes(habit.key) })),
    },
    text<HabitRow>('why', 'Why it matters in this role', 'minmax(0, 2fr)'),
  ];
  const lead = value.leadership;
  const setLead = (next: Partial<IdealProfile['leadership']>): void => onChange({ ...value, leadership: { ...lead, ...next } });
  const parts: Readonly<Record<IdealProfileStep, ReactNode>> = {
    tools: (
      <TemplateBoxFrame box={boxes.tools} problem={problemOf(progress, 'tools')} isCounted>
        <ToolRows
          box={boxes.tools}
          rows={value.tools}
          onChange={set('tools')}
          disabled={disabled}
          renderName={(name) => (
            <SuggestInput aria-label={name.ariaLabel} placeholder={name.placeholder} disabled={name.disabled} value={name.value} onChange={name.onChange} suggestions={toolNames} maxLength={TOOL_NAME_MAX} />
          )}
        />
      </TemplateBoxFrame>
    ),
    mustHaves: (
      <TemplateBoxFrame box={boxes.mustHaves} problem={problemOf(progress, 'mustHaves')} isCounted>
        <TemplateRows box={boxes.mustHaves} rows={value.mustHaves} columns={MUST_COLUMNS} emptyRow={() => ({ skill: '', check: null })} onChange={set('mustHaves')} disabled={disabled} />
      </TemplateBoxFrame>
    ),
    niceToHaves: (
      <TemplateBoxFrame box={boxes.niceToHaves} problem={problemOf(progress, 'niceToHaves')} isCounted={value.niceToHaves.length > 0}>
        <TemplateRows box={boxes.niceToHaves} rows={value.niceToHaves} columns={[text<TextRow>('text', 'Nice-to-have')]} emptyRow={emptyText} onChange={set('niceToHaves')} disabled={disabled} />
      </TemplateBoxFrame>
    ),
    experience: (
      <TemplateBoxFrame box={boxes.experience} problem={problemOf(progress, 'experience')} isCounted>
        <TemplateFields
          box={boxes.experience}
          value={value.experience}
          isRequired
          disabled={disabled}
          columns={[
            { key: 'yearsMin', label: 'Years, at least', kind: 'number', placeholder: '1' },
            { key: 'yearsMax', label: 'Years, at most', kind: 'number', placeholder: '3' },
            { key: 'workType', label: 'Type of work', kind: 'text', placeholder: 'Customer support or account management' },
            { key: 'companyType', label: 'Type of company', kind: 'text', placeholder: 'BPO serving US or UK clients' },
          ]}
          onChange={set('experience')}
        />
      </TemplateBoxFrame>
    ),
    qualifications: (
      <TemplateBoxFrame box={boxes.qualifications} problem={problemOf(progress, 'qualifications')} isCounted={value.qualifications.length > 0}>
        <TemplateRows box={boxes.qualifications} rows={value.qualifications} columns={[text<TextRow>('text', 'Requirement')]} emptyRow={emptyText} onChange={set('qualifications')} disabled={disabled} />
      </TemplateBoxFrame>
    ),
    dealBreakers: (
      <TemplateBoxFrame box={boxes.dealBreakers} problem={problemOf(progress, 'dealBreakers')} isCounted={value.dealBreakers.length > 0}>
        <TemplateRows box={boxes.dealBreakers} rows={value.dealBreakers} columns={[text<TextRow>('text', 'Deal-breaker')]} emptyRow={emptyText} onChange={set('dealBreakers')} disabled={disabled} />
      </TemplateBoxFrame>
    ),
    workHabits: (
      <TemplateBoxFrame box={boxes.workHabits} problem={problemOf(progress, 'workHabits')} isCounted>
        <TemplateRows box={boxes.workHabits} rows={value.workHabits} columns={habitColumns} emptyRow={() => ({ habit: null, why: '' })} onChange={set('workHabits')} disabled={disabled} />
        {managesPeople && <p className={styles.aside}>Develops others and Decides without all the facts are added to this box from the Leadership add-on below.</p>}
      </TemplateBoxFrame>
    ),
    redFlags: (
      <TemplateBoxFrame box={boxes.redFlags} problem={problemOf(progress, 'redFlags')} isCounted={value.redFlags.length > 0}>
        <TemplateRows box={boxes.redFlags} rows={value.redFlags} columns={[text<TextRow>('text', 'Red flag to watch for')]} emptyRow={emptyText} onChange={set('redFlags')} disabled={disabled} />
      </TemplateBoxFrame>
    ),
    leadership: (
      <>
        <AddOnHeading title="Leadership add-on, Part 1">This role manages people, so these join the profile.</AddOnHeading>
        <TemplateBoxFrame box={boxes.leadExperience} problem={problemOf(progress, 'leadExperience')} isCounted>
          <TemplateFields
            box={boxes.leadExperience}
            value={{ mostManaged: lead.mostManaged, yearsManaging: lead.yearsManaging, teamType: lead.teamType }}
            isRequired
            disabled={disabled}
            columns={[
              { key: 'mostManaged', label: 'Most people managed before', kind: 'number' },
              { key: 'yearsManaging', label: 'Years managing people', kind: 'number' },
              { key: 'teamType', label: 'Type of team', kind: 'text' },
            ]}
            onChange={setLead}
          />
        </TemplateBoxFrame>
        <TemplateBoxFrame box={boxes.leadSituation} problem={problemOf(progress, 'leadSituation')} isCounted>
          <TemplateFields
            box={boxes.leadSituation}
            value={{ situation: lead.situation, strongAnswer: lead.strongAnswer }}
            isRequired
            disabled={disabled}
            columns={[
              { key: 'situation', label: 'The situation', kind: 'long' },
              { key: 'strongAnswer', label: 'What a strong answer includes', kind: 'long' },
            ]}
            onChange={setLead}
          />
        </TemplateBoxFrame>
        <TemplateBoxFrame box={boxes.leadHabits} problem={problemOf(progress, 'leadHabits')} isCounted>
          <TemplateFields
            box={boxes.leadHabits}
            value={{ developsOthersWhy: lead.developsOthersWhy, decidesWhy: lead.decidesWhy }}
            isRequired
            disabled={disabled}
            columns={[
              { key: 'developsOthersWhy', label: 'Develops others: why it matters in this role', kind: 'text', placeholder: 'They will train two new executives this year' },
              { key: 'decidesWhy', label: 'Decides without all the facts: why it matters', kind: 'text', placeholder: 'Clients call at night with no one else on shift' },
            ]}
            onChange={setLead}
          />
        </TemplateBoxFrame>
      </>
    ),
  };
  const shown = step ? [step] : idealProfileStepsFor(managesPeople);
  return (
    <div className={styles.boxes}>
      {shown.map((key) => (
        <Fragment key={key}>{parts[key]}</Fragment>
      ))}
    </div>
  );
}

// --- The 30-day plan --------------------------------------------------------------------------------------------------

const SKILL_COLUMNS: readonly TemplateColumn<SkillRow>[] = [
  text<SkillRow>('canDo', 'They can…', 'minmax(0, 2.4fr)'),
  { key: 'byDay', label: 'By day', kind: 'day', track: 'minmax(0, 0.9fr)' },
  { key: 'check', label: 'How you will check', kind: 'choice', options: PLAN_CHECKS.map((value) => ({ value, label: PLAN_CHECK_LABELS[value] })) },
  text<SkillRow>('checkedBy', 'Checked by'),
];
const DELIVERED_COLUMNS: readonly TemplateColumn<DeliveredRow>[] = [
  text<DeliveredRow>('work', 'Work delivered', 'minmax(0, 2fr)'),
  text<DeliveredRow>('howMuch', 'How much'),
  text<DeliveredRow>('qualityBar', 'Quality bar'),
  text<DeliveredRow>('whereChecked', 'Where it is checked'),
];
const MEET_COLUMNS: readonly TemplateColumn<MeetRow>[] = [text<MeetRow>('who', 'Person, team, client or vendor'), text<MeetRow>('why', 'Why', 'minmax(0, 2fr)')];

export function Plan30Form({ value, onChange, managesPeople, disabled = false }: FormProps<Plan30>): ReactNode {
  const boxes = PLAN_30_BOXES;
  const progress = plan30Progress(value, managesPeople);
  const set = <K extends keyof Plan30>(key: K) => (next: Plan30[K]): void => onChange({ ...value, [key]: next });
  const lead = value.leadership;
  const setLead = (next: Partial<Plan30['leadership']>): void => onChange({ ...value, leadership: { ...lead, ...next } });
  return (
    <div className={styles.boxes}>
      <TemplateBoxFrame box={boxes.skills} problem={problemOf(progress, 'skills')} isCounted>
        <TemplateRows box={boxes.skills} rows={value.skills} columns={SKILL_COLUMNS} emptyRow={() => ({ canDo: '', byDay: null, check: null, checkedBy: '' })} onChange={set('skills')} disabled={disabled} />
      </TemplateBoxFrame>
      <TemplateBoxFrame box={boxes.delivered} problem={problemOf(progress, 'delivered')} isCounted>
        <TemplateRows box={boxes.delivered} rows={value.delivered} columns={DELIVERED_COLUMNS} emptyRow={() => ({ work: '', howMuch: '', qualityBar: '', whereChecked: '' })} onChange={set('delivered')} disabled={disabled} />
      </TemplateBoxFrame>
      <TemplateBoxFrame box={boxes.rampUp} problem={problemOf(progress, 'rampUp')} isCounted>
        <TemplateFields
          box={boxes.rampUp}
          value={value.rampUp}
          isRequired
          disabled={disabled}
          columns={[
            { key: 'measure', label: 'Measure', kind: 'text' },
            { key: 'day30Target', label: 'Day 30 target', kind: 'text' },
            { key: 'day90Target', label: 'Full target at Day 90', kind: 'text' },
            { key: 'whereTracked', label: 'Where it is tracked', kind: 'text' },
          ]}
          onChange={set('rampUp')}
        />
      </TemplateBoxFrame>
      <TemplateBoxFrame box={boxes.meet} problem={problemOf(progress, 'meet')} isCounted={value.meet.length > 0}>
        <TemplateRows box={boxes.meet} rows={value.meet} columns={MEET_COLUMNS} emptyRow={() => ({ who: '', why: '' })} onChange={set('meet')} disabled={disabled} />
      </TemplateBoxFrame>
      <TemplateBoxFrame box={boxes.support} problem={problemOf(progress, 'support')} isCounted>
        <TemplateFields
          box={boxes.support}
          value={value.support}
          isRequired
          disabled={disabled}
          columns={[
            { key: 'trainer', label: 'Who trains them', kind: 'text' },
            { key: 'meetingRhythm', label: 'How often you meet', kind: 'text' },
            { key: 'comeToYouFor', label: 'What they can come to you for', kind: 'text' },
          ]}
          onChange={set('support')}
        />
      </TemplateBoxFrame>

      {managesPeople && (
        <>
          <AddOnHeading title="Leadership add-on, Part 2">This role manages people, so these join the 30-day plan.</AddOnHeading>
          <TemplateBoxFrame box={boxes.leadTeam} problem={problemOf(progress, 'leadTeam')} isCounted>
            <TemplateFields
              box={boxes.leadTeam}
              value={{ teamSize: lead.teamSize, teamRoles: lead.teamRoles }}
              isRequired
              disabled={disabled}
              columns={[
                { key: 'teamSize', label: 'Number of people', kind: 'number' },
                { key: 'teamRoles', label: 'Their roles', kind: 'text' },
              ]}
              onChange={setLead}
            />
          </TemplateBoxFrame>
          <TemplateBoxFrame box={boxes.leadFixed} problem={problemOf(progress, 'leadFixed')} isCounted>
            <TemplateFields
              box={boxes.leadFixed}
              value={{ oneToOnesBy: lead.oneToOnesBy, teamReportBy: lead.teamReportBy, targetsAgreedBy: lead.targetsAgreedBy }}
              isRequired
              disabled={disabled}
              columns={LEADER_FIXED_ITEMS.map((item) => ({ key: item.key, label: item.label, kind: 'day' as const }))}
              onChange={setLead}
            />
          </TemplateBoxFrame>
          <TemplateBoxFrame box={boxes.leadProblem} problem={problemOf(progress, 'leadProblem')} isCounted>
            <TemplateFields box={boxes.leadProblem} value={{ biggestProblem: lead.biggestProblem }} isRequired disabled={disabled} columns={[{ key: 'biggestProblem', label: 'The problem', kind: 'long' }]} onChange={setLead} />
          </TemplateBoxFrame>
        </>
      )}
    </div>
  );
}

// --- The 90-day plan --------------------------------------------------------------------------------------------------

const NUMBER_COLUMNS: readonly TemplateColumn<NumberRow>[] = [
  text<NumberRow>('measure', 'Measure', 'minmax(0, 1.6fr)'),
  text<NumberRow>('target', 'Target', 'minmax(0, 1.4fr)'),
  text<NumberRow>('whereTracked', 'Where it is tracked'),
  text<NumberRow>('checkedBy', 'Checked by'),
];
const TEAM_COLUMNS: readonly TemplateColumn<TeamMeasureRow>[] = [
  text<TeamMeasureRow>('measure', 'Team measure', 'minmax(0, 1.6fr)'),
  text<TeamMeasureRow>('today', 'Today'),
  text<TeamMeasureRow>('day90Target', 'Day 90 target'),
  text<TeamMeasureRow>('whereTracked', 'Where it is tracked'),
];

export function Plan90Form({ value, onChange, managesPeople, disabled = false }: FormProps<Plan90>): ReactNode {
  const boxes = PLAN_90_BOXES;
  const progress = plan90Progress(value, managesPeople);
  const set = <K extends keyof Plan90>(key: K) => (next: Plan90[K]): void => onChange({ ...value, [key]: next });
  const setLead = <K extends keyof Plan90['leadership']>(key: K) => (next: Plan90['leadership'][K]): void => onChange({ ...value, leadership: { ...value.leadership, [key]: next } });
  const hasImprovement = value.improvement.problem !== '' || value.improvement.doneLooksLike !== '' || value.improvement.byDay !== null;
  return (
    <div className={styles.boxes}>
      <p className={styles.aside}>Nothing here repeats an item from the 30-day plan.</p>
      <TemplateBoxFrame box={boxes.owns} problem={problemOf(progress, 'owns')} isCounted>
        <TemplateRows box={boxes.owns} rows={value.owns} columns={[text<TextRow>('text', 'They own…')]} emptyRow={emptyText} onChange={set('owns')} disabled={disabled} />
      </TemplateBoxFrame>
      <TemplateBoxFrame box={boxes.numbers} problem={problemOf(progress, 'numbers')} isCounted>
        <TemplateRows box={boxes.numbers} rows={value.numbers} columns={NUMBER_COLUMNS} emptyRow={() => ({ measure: '', target: '', whereTracked: '', checkedBy: '' })} onChange={set('numbers')} disabled={disabled} />
      </TemplateBoxFrame>
      <TemplateBoxFrame box={boxes.improvement} rule={managesPeople ? 'exactly 1 (this role manages people)' : 'optional for a role that manages nobody'} problem={problemOf(progress, 'improvement')} isCounted={managesPeople || hasImprovement}>
        <TemplateFields
          box={boxes.improvement}
          value={value.improvement}
          isRequired={managesPeople}
          disabled={disabled}
          columns={[
            { key: 'problem', label: 'The problem today', kind: 'text' },
            { key: 'doneLooksLike', label: 'What done looks like', kind: 'text' },
            { key: 'byDay', label: 'By day', kind: 'day' },
          ]}
          onChange={set('improvement')}
        />
      </TemplateBoxFrame>
      <TemplateBoxFrame box={boxes.warningSigns} problem={problemOf(progress, 'warningSigns')} isCounted>
        <TemplateRows box={boxes.warningSigns} rows={value.warningSigns} columns={[text<TextRow>('text', 'Warning sign')]} emptyRow={emptyText} onChange={set('warningSigns')} disabled={disabled} />
      </TemplateBoxFrame>
      <TemplateBoxFrame box={boxes.greatHire} problem={problemOf(progress, 'greatHire')} isCounted>
        <TemplateRows box={boxes.greatHire} rows={value.greatHire} columns={[text<TextRow>('text', 'Result by month 12')]} emptyRow={emptyText} onChange={set('greatHire')} disabled={disabled} />
      </TemplateBoxFrame>

      {managesPeople && (
        <>
          <AddOnHeading title="Leadership add-on, Part 3">These team targets sit beside the leader’s own numbers in box 2. Both count at the Day 90 review.</AddOnHeading>
          <TemplateBoxFrame box={boxes.leadMeasures} problem={problemOf(progress, 'leadMeasures')} isCounted>
            <TemplateRows box={boxes.leadMeasures} rows={value.leadership.teamMeasures} columns={TEAM_COLUMNS} emptyRow={() => ({ measure: '', today: '', day90Target: '', whereTracked: '' })} onChange={setLead('teamMeasures')} disabled={disabled} />
          </TemplateBoxFrame>
          <TemplateBoxFrame box={boxes.leadInPlace} problem={problemOf(progress, 'leadInPlace')} isCounted>
            <TemplateRows box={boxes.leadInPlace} rows={value.leadership.inPlace} columns={[text<TextRow>('text', 'In place by Day 90')]} emptyRow={emptyText} onChange={setLead('inPlace')} disabled={disabled} />
          </TemplateBoxFrame>
        </>
      )}
    </div>
  );
}
